import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Gavel as GavelIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Checklist as ChecklistIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  drawerWidth: number;
  variant: 'temporary' | 'permanent';
}

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
}

const mainNavItems: NavItem[] = [
  { text: '案件一覧', icon: <FolderIcon />, path: '/projects' },
  { text: '新規案件作成', icon: <AddIcon />, path: '/projects/new' },
  { text: 'リスク分析', icon: <AssessmentIcon />, path: '/analysis' },
  { text: '法令データベース', icon: <GavelIcon />, path: '/laws' },
  { text: '設定', icon: <SettingsIcon />, path: '/settings' },
];

const adminNavItems: NavItem[] = [
  { text: 'ユーザー管理', icon: <PeopleIcon />, path: '/admin/users', adminOnly: true },
  { text: '全案件管理', icon: <DashboardIcon />, path: '/admin/projects', adminOnly: true },
  { text: 'マスターデータ', icon: <ChecklistIcon />, path: '/admin/master', adminOnly: true },
];

export function Sidebar({ open, onClose, drawerWidth, variant }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
    if (variant === 'temporary') {
      onClose();
    }
  };

  const drawer = (
    <Box sx={{ overflow: 'auto' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AdminIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          M&A対応
        </Typography>
      </Box>

      <Divider />

      <List sx={{ px: 1 }}>
        {mainNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {isAdmin && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="overline"
            sx={{ px: 3, py: 1, display: 'block', color: 'text.secondary' }}
          >
            管理者メニュー
          </Typography>
          <List sx={{ px: 1 }}>
            {adminNavItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                >
                  <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={variant === 'permanent' ? true : open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      {drawer}
    </Drawer>
  );
}
